import {
  defineComponent, ref, computed, PropType,
} from 'vue'
import { Text } from '../Text'
import { Tooltip } from '../Popover'
import { Icon } from '../Icon'
import { Close } from '../../icons'

import { TagSize, TagTheme, TagColor } from '../../enums'

export default defineComponent({
  props: {
    /**
     * 标签尺寸
     */
    size: {
      type: String as PropType<TagSize>,
      default: 'default',
    },
    /**
     * 标签风格
     */
    theme: {
      type: String as PropType<TagTheme>,
      default: 'light',
    },
    /**
     * 标签颜色
     */
    color: {
      type: String as PropType<TagColor>,
      default: 'grey',
    },
    /**
     * 标签是否可关闭
     */
    closeable: {
      type: Boolean,
      default: false,
    },
    /**
     * 控制标签展示
     */
    visible: {
      type: Boolean,
      default: true,
    },
    /**
     * 控制标签最大长度
     */
    maxWidth: {
      type: Number,
      default: undefined,
    },
    /**
     * 标签禁用状态
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 标签加粗
     */
    bold: {
      type: Boolean,
      default: true,
    },
    /**
     * 标签自定义颜色
     */
    custom: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    close() {
      return true
    },
    click(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    mouseenter(e: MouseEvent) {
      return e !== undefined
    },
    mouseleave(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
  },
  setup(props, { slots, emit }) {
    const cmpClass = computed(
      () => {
        const general = [
          'd-tag',
          `d-tag-${props.size}`,
          `d-tag-theme-${props.theme}`,
        ]

        if (props.custom) {
          return general
        }

        return [
          ...general,
          `d-tag-color-${props.color}`,
          ...props.color === 'white'
            ? [
              '--color-text-paragraph',
              '--color-bg-white',
            ]
            : [],
          ...props.theme === 'solid'
            ? [
              '--color-white',
              `--color-bg-${props.color}-6`,
            ]
            : [
              `--color-${props.color}-8`,
            ],
        ]
      },
    )

    const bindings = {
      onClick: (e: MouseEvent) => emit('click', e),
      onMousedown: (e: MouseEvent) => emit('mousedown', e),
      onMouseenter: (e: MouseEvent) => emit('mouseenter', e),
      onMouseleave: (e: MouseEvent) => emit('mouseleave', e),
      onMouseup: (e: MouseEvent) => emit('mouseup', e),
    }

    const tag = ref()
    const text = ref()

    return () => props.visible
    && <span
      class={cmpClass.value}
      style={
        props.maxWidth !== undefined
          ? {
            maxWidth: `${props.maxWidth}px`,
          }
          : undefined
      }
      ref={tag}
      {...bindings}
    >
      <Text
        ref={text}
        class={'d-tag-content'}
        bold={props.bold}
        ellipsis
        size={props.size}
        color={'current'}
      >
        {slots.default?.()}
      </Text>
      {
        props.closeable && !props.disabled && <Icon
          class={
            [
              'd-tag-close',
              'd-clickable',
            ]
          }
          icon={Close}
          size={props.size}
          color={
            props.theme === 'light'
              ? 'text-description'
              : 'current'
          }
          {
            ...{
              onClick(e: MouseEvent) {
                emit('close', e)
              },
            }
          }
        />
      }
      <Tooltip
        target={tag.value}
        validate={text.value?.isEllipsisActive}
        v-slots={{
          content: slots.default,
        }}
      />
    </span>
  },
})
